import React, {useEffect, useState} from "react";

import ShortcutElementStyle from './ShortcutElement.module.css';
import '../../index.css'
import api from "../../services/api";
import { ReactComponent as LoaderIcon } from '../../assets/images/loader.svg';
import {useAppContext} from "../../provider/appProvider";
import {getSystemID} from "../../provider/authProvider";

const DEFAULT_ICON = 'https://vault.watch4safe.fr/public/assets/icons/process.svg';
const IPX_REFRESH_INTERVAL = 5000;
const SHORT_URL_REFRESH_INTERVAL = 5000;

const equalsIgnoreCase = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined
  && String(a).toLowerCase() === String(b).toLowerCase();

const withUnit = (value, unit) => (unit ? `${value} ${unit}` : `${value}`);

/**
 * Un raccourci Zigbee est actif quand l'état de l'expose piloté vaut value_on.
 */
const getZigbeeIsActive = (shortcut) => {
  const expose = shortcut.zigbeeExpose;
  if (!expose) {
    return true;
  }
  if (!expose.state) {
    return false;
  }
  if (expose.value_on === null || expose.value_on === undefined) {
    return true;
  }
  return equalsIgnoreCase(expose.state.value, expose.value_on);
};

/**
 * zigbeeDisplayExpose (optionnel) prime sur zigbeeExpose : permet d'afficher un expose
 * différent de celui piloté au clic (ex : afficher "power" tout en pilotant "state").
 */
const getZigbeeStatus = (shortcut) => {
  const expose = shortcut.zigbeeDisplayExpose ?? shortcut.zigbeeExpose;
  if (!expose.state) {
    return 'N/A';
  }

  const value = expose.state.value;
  if (equalsIgnoreCase(value, expose.value_on)) {
    return shortcut.title_status_active ?? value;
  }
  if (equalsIgnoreCase(value, expose.value_off)) {
    return shortcut.title_status_default ?? value;
  }
  return withUnit(value, expose.unit);
};

const ShortcutElement = (props) => {
  const { shortcut, type = 0 } = props;
  const {
    id,
    name,
    title_status_default: status,
    title_status_active: statusActive,
    infos_shorts_urls_id: infosURLTag,
    default_shorts_urls_id: URLTag,
    active_shorts_urls_id: activeURTag,
    ipxEntity,
  } = shortcut;
  const icon = shortcut.defaultShortcuts ? shortcut.defaultShortcuts.link : null;
  const iconActive = shortcut.activeShortcuts ? shortcut.activeShortcuts.link : null;

  const isIpx = Boolean(ipxEntity);
  const isZigbee = !isIpx && Boolean(shortcut.zigbeeExpose || shortcut.zigbeeDisplayExpose);

  const [shortUrlActive, setShortUrlActive] = useState(null);
  const [ipxData, setIpxData] = useState(null);
  const [ipxError, setIpxError] = useState(false);

  const { fetchShortcuts } = useAppContext();

  const verifyIsActive = async (urlInfosTag) => {
    try {
      const response = await api.get(`/short-urls/${urlInfosTag}/execute`);
      setShortUrlActive(Boolean(response.data));
    } catch (error) {
      console.log((error.response && error.response.data) || error.message || error.toString());
    }
  };

  useEffect(() => {
    if (isIpx || isZigbee) {
      return;
    }

    if (infosURLTag) {
      const intervalId = setInterval(() => {
        verifyIsActive(infosURLTag);
      }, SHORT_URL_REFRESH_INTERVAL);
      verifyIsActive(infosURLTag);

      return () => {
        clearInterval(intervalId);
      };
    } else {
      setShortUrlActive(true);
    }
  }, [infosURLTag, isIpx, isZigbee]);

  const ipxId = ipxEntity ? ipxEntity.ipx_id : null;
  const ipxEntityId = ipxEntity ? ipxEntity.id : null;

  useEffect(() => {
    if (!ipxId || !ipxEntityId) {
      return;
    }

    let cancelled = false;
    const fetchIpxValue = async () => {
      try {
        const response = await api.get(`/systems/${getSystemID()}/ipx/${ipxId}/entities/${ipxEntityId}/value`);
        if (!cancelled) {
          setIpxData(response.data);
          setIpxError(false);
        }
      } catch (error) {
        // 502/503 : IPX injoignable ou accès externe désactivé
        if (!cancelled) {
          setIpxError(true);
        }
      }
    };

    fetchIpxValue();
    const intervalId = setInterval(fetchIpxValue, IPX_REFRESH_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [ipxId, ipxEntityId]);

  let isActive;
  let statusText;
  if (isIpx) {
    isActive = true;
    if (ipxData && ipxData.value !== null && ipxData.value !== undefined) {
      statusText = withUnit(ipxData.value, ipxData.unit ?? ipxEntity.unit);
    } else if (ipxError) {
      statusText = 'Indisponible';
    } else {
      statusText = 'Chargement...';
    }
  } else if (isZigbee) {
    isActive = getZigbeeIsActive(shortcut);
    statusText = getZigbeeStatus(shortcut);
  } else {
    isActive = shortUrlActive;
    statusText = isActive ? (statusActive ?? 'Inconnu') : (status ?? 'Inconnu');
  }

  const isLoading = isActive === null;
  const isDataShortcut = isIpx || isZigbee;
  const iconSrc = isActive ? (iconActive ?? DEFAULT_ICON) : (icon ?? DEFAULT_ICON);

  const handleCLickShortcut = async (event) => {
    try {
      if (shortcut.zigbeeExpose) {
        await api.post(`/shortcuts/${id}/trigger`);
        await fetchShortcuts();
        return;
      }

      if (!URLTag || isActive === null) {
        return;
      }

      if (!activeURTag) {
        await api.get(`/short-urls/${URLTag}/execute`);
        return;
      }

      if (isActive) {
        await api.get(`/short-urls/${activeURTag}/execute`);
      } else {
        await api.get(`/short-urls/${URLTag}/execute`);
      }
      if (infosURLTag) {
        verifyIsActive(infosURLTag);
      }
    } catch (error) {
      console.log((error.response && error.response.data) || error.message || error.toString());
    }
  }

  return (
    <>
      { type === 0 && (
        <div className={ShortcutElementStyle.element} onClick={handleCLickShortcut}>
          {isLoading && (
            <>
              <LoaderIcon className={'loader'} alt={name} height={20} width={20}/>
              <div className={ShortcutElementStyle.nameContainer}>
                <span className={ShortcutElementStyle.name}>Chargement...</span>
              </div>
            </>
          )}

          {!isLoading && (
            <>
              <img src={iconSrc} alt={name} height={20} width={20}/>
              <div className={ShortcutElementStyle.nameContainer}>
                <span className={ShortcutElementStyle.name}>{isDataShortcut ? `${name} - ${statusText}` : name}</span>
              </div>
            </>
          )}
        </div>
      )}

      {type === 1 && (
        <div className={ShortcutElementStyle.element02} onClick={handleCLickShortcut}>
          {isLoading && (
            <>
              <LoaderIcon className={'loader'} alt={name} height={40} width={40}/>
              <span className={ShortcutElementStyle.name02}>{'Chargement...'}</span>
              <span className={ShortcutElementStyle.status}>{'Inconnu'}</span>
            </>
          )}

          {!isLoading && (
            <>
              <img src={iconSrc} alt={name} height={40} width={40}/>
              <span className={ShortcutElementStyle.name02}>{name}</span>
              <span className={ShortcutElementStyle.status}>{statusText}</span>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ShortcutElement;
